import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  SimpleChanges,
  forwardRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as CodeMirror from 'codemirror';
import 'codemirror/addon/edit/matchbrackets.js';
import 'codemirror/addon/edit/closebrackets.js';
import 'codemirror/addon/search/match-highlighter.js';
import 'codemirror/addon/hint/show-hint.js';

import { EquationEditorHelper } from './equation-editor-helper';
import './mode/MatrixNutri.js';

const defaultConfig = {
  mode: 'MatrixNutri',
  lineNumbers: true,
  indentAuto: true,
  smartIndent: true,
  indentUnit: 4,
  lineWrapping: true,
  matchBrackets: true,
  autoCloseBrackets: true
};

@Component({
  selector: 'neq-equation-editor',
  template: '<textarea #host></textarea>',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NeqEquationEditorComponent),
      multi: true
    }
  ]
})
export class NeqEquationEditorComponent implements AfterViewInit, ControlValueAccessor, OnChanges {

  @Input() value = '';
  @Input() config = defaultConfig;
  @Input() readOnly = false;
  @Input() formatting = true;

  @Output() change = new EventEmitter<string>();
  @Output() refresh = new EventEmitter();
  @Output() focus = new EventEmitter();
  @Output() blur = new EventEmitter();

  @ViewChild('host') host;

  private codemirror = undefined;
  private helper: EquationEditorHelper;

  // ControlValueAccessor functions
  private onChanges: (_: any) => {};
  private onTouched: () => {};


  ngAfterViewInit() {
    this.initCodemirror();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Change editor value when input changed
    // (and format it if formatting enabled)
    if (changes['value']) {
      this.value = changes['value'].currentValue;

      if (this.codemirror && this.formatting) {
        this.format();
      }

      // Call onChanges from ControlValueAccessor (for FormControl)
      if (this.onChanges) {
        this.onChanges(this.value);
      }
    }

    // Change the editor read only property when input changed
    if (changes['readOnly'] && this.codemirror) {
      this.codemirror.setOption('readOnly', changes['readOnly'].currentValue);
      this.codemirror.setOption('theme', 'default readonly');
    }

    // Format the editor content if formatting input set to true
    if (changes['formatting']) {
      this.format();
    }

    // Update editor config when input changed
    if (changes['config']) {
      // Add missing options of the default config in the new config
      for(const k of Object.keys(defaultConfig)) {
        if (this.config[k] === undefined) {
          this.config[k] = defaultConfig[k];
        }
      }

      if (this.codemirror) {
        Object.keys(this.config).forEach(k => this.codemirror.setOption(k, this.config[k]));
      }
    }
  }

  /**
   * Initialize the codemirror editor
   */
  initCodemirror() {
    this.codemirror = CodeMirror.fromTextArea(this.host.nativeElement, this.config);
    this.codemirror.setValue(this.value);
    this.codemirror.setOption('readOnly', this.readOnly);

    this.helper = new EquationEditorHelper(this.codemirror);
    this.format();


    if (this.formatting) {
      this.helper.registerWrappedLineIndenter();
    }
    if (this.readOnly) {
      this.codemirror.setOption('theme', 'default readonly');
    } else {
      this.codemirror.setOption('theme', 'default');
      // Enable autocompletion on Ctrl-Space
      this.codemirror.setOption('extraKeys', { 'Ctrl-Space': 'autocomplete' });
      this.codemirror.setOption('hintOptions', { hint: this.helper.equationHint });
      this.helper.registerAutocompletionListener();
    }

    this.registerEventListeners();
  }

  private registerEventListeners() {
    this.codemirror.on('change', () => {
      const newValue = this.codemirror.getValue();

      this.value = newValue;
      this.change.emit(newValue);

      // Call onChanges from ControlValueAccessor (for FormControl)
      if (this.onChanges) {
        this.onChanges(newValue);
      }
    });

    this.codemirror.on('focus', () => {
      this.focus.emit();
    });

    this.codemirror.on('blur', () => {
      this.blur.emit();

      // Call onTouched from ControlValueAccessor (for FormControl)
      if (this.onTouched) {
        this.onTouched();
      }
    });
  }

  writeValue(obj: any): void {
    this.value = obj || '';

    if (this.codemirror) {
      this.codemirror.setValue(this.value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChanges = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Insert text at the cursor position in the editor
   */
  insert(value: string) {
    if (this.codemirror) {
      const firstComma = value.indexOf(',');
      const closingComma = value.indexOf(')');
      let offset = 0;
      if ((firstComma >= 0)) {
        offset = firstComma
      } else if ((closingComma >= 0)) {
        offset = closingComma
      } else {
        offset = value.length;
      }
      this.helper.insertTextAtCursorPos(value, offset);
    }
  }

  /**
   * Format the equation in the editor
   */
  format() {
    if (this.codemirror) {
      if (this.formatting) {
        this.helper.formatAndIndent();
      }
      this.refresh.next(this.value);
    }
  }

}
